/**
 * Shared Executive Summary building blocks: the freshness badge, the five
 * KPI tiles, and the generic rows-list panel reused across
 * vehicle-flow/backup-readiness/jobs/synthetic/delivery-watchdog/
 * recent-changes — all six endpoints share the same
 * `{ measuredAt, freshnessSeconds, status, rows }` envelope per the GTCC
 * 1:1 program contract.
 */
import { useLocale } from "@genius-tracker/i18n/react";
import type { ReactNode } from "react";

import type { GtccRecentChangeRowDto, GtccRowDto, GtccRowVerdict } from "../../../exec-types.js";
import {
  FreshBadge as PrimFreshBadge,
  Tile as PrimTile,
  type CockpitTone,
} from "../../../ui/primitives.js";

function toCockpitTone(tone: GtccRowVerdict): CockpitTone {
  return tone === "unmeasurable" ? "muted" : tone;
}

/**
 * Thin adapter over `ui/primitives.tsx`'s `FreshBadge` (`.fresh`/`.fresh.stale`
 * pill) — same pattern as `OvwView.tsx`'s `ControlFreshBadge`. No own markup:
 * the outer `span` only carries the test hooks this module's tests key off.
 */
export function FreshBadge(props: {
  freshnessSeconds: number;
  stale?: boolean;
  label?: string;
  testId?: string;
}): ReactNode {
  const { t } = useLocale();
  const stale = props.stale === true;
  const measurable =
    typeof props.freshnessSeconds === "number" && Number.isFinite(props.freshnessSeconds);
  const label =
    props.label ??
    (measurable
      ? t("admin.gtcc.cockpit.exec.fresh_ago", { seconds: props.freshnessSeconds })
      : t("admin.gtcc.cockpit.exec.fresh_unavailable"));
  return (
    <span
      data-testid={props.testId ?? "gtcc-cockpit-fresh-badge"}
      data-stale={stale ? "true" : "false"}
    >
      <PrimFreshBadge label={label} stale={stale} />
    </span>
  );
}

/**
 * Thin adapter over `ui/primitives.tsx`'s `Tile` (`.card` + `.card::before`
 * colored left edge, `.big`, `.st`) — same pattern as `OvwView.tsx`'s
 * `ControlTile`. No own card shape: colored border, big value and colored
 * subline all come from the canonical `.card`/`.big`/`.st` CSS contract.
 */
export function Tile(props: {
  testId: string;
  tone: GtccRowVerdict;
  title: string;
  freshBadge?: ReactNode;
  big: ReactNode;
  small?: ReactNode;
  status: string;
}): ReactNode {
  const tone = toCockpitTone(props.tone);
  return (
    <div data-testid={props.testId} data-tone={tone}>
      <PrimTile
        tone={tone}
        title={props.title}
        badge={props.freshBadge}
        value={
          props.small !== undefined ? (
            <>
              {props.big}
              {props.small}
            </>
          ) : (
            props.big
          )
        }
        status={props.status}
      />
    </div>
  );
}

const DOT_COLOR: Record<GtccRowVerdict, string> = {
  ok: "var(--gt-sys-success)",
  warn: "var(--gt-sys-warning)",
  crit: "var(--gt-sys-danger)",
  info: "var(--gt-sys-info)",
  unmeasurable: "var(--gt-sys-text-muted)",
};

function Dot(props: { verdict: GtccRowVerdict }): ReactNode {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: DOT_COLOR[props.verdict],
        marginInlineEnd: 6,
      }}
    />
  );
}

export function RowsPanel(props: {
  title: string;
  freshBadge?: ReactNode;
  rows: ReadonlyArray<GtccRowDto | GtccRecentChangeRowDto>;
  emptyLabel: string;
  testId: string;
  /** Set when the envelope's `truncated` is `true` — the row count shown is not the full count. */
  truncatedNote?: string;
}): ReactNode {
  return (
    <div data-testid={props.testId}>
      <h3
        style={{
          margin: 0,
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          fontSize: "var(--gt-sys-font-size-md)",
        }}
      >
        {props.title}
        {props.freshBadge}
      </h3>
      {props.rows.length === 0 ? (
        <p style={{ color: "var(--gt-sys-text-muted)" }} data-testid={`${props.testId}-empty`}>
          {props.emptyLabel}
        </p>
      ) : (
        props.rows.map((row, index) => (
          <div
            key={`${row.key}-${index}`}
            data-testid={`${props.testId}-row-${row.key}`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 0",
            }}
          >
            <span>
              <Dot verdict={row.verdict} />
              {row.label}
            </span>
            <span style={{ fontFamily: "var(--gt-sys-font-family-mono)" }}>{row.valueText}</span>
          </div>
        ))
      )}
      {props.truncatedNote !== undefined ? (
        <p style={{ color: "var(--gt-sys-text-muted)" }} data-testid={`${props.testId}-truncated`}>
          {props.truncatedNote}
        </p>
      ) : null}
    </div>
  );
}
